import type { CreateProformaDto, CustomerOptionDto, ProformaDto, ProformaListDto, UpdateProformaDto } from './models';
import type { ProformaStatus } from './proforma-status.enum';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from '../models';
import type { TemplateDto } from '../templates/models';

@Injectable({
  providedIn: 'root',
})
export class ProformaService {
  apiName = 'Default';
  

  create = (input: CreateProformaDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProformaDto>({
      method: 'POST',
      url: '/api/app/proforma',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  customerEdit = (projectId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, boolean>({
      method: 'POST',
      url: `/api/app/proforma/customer-edit/${projectId}`,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/proforma/${id}`,
    },
    { apiName: this.apiName,...config });
  

  generateAcknowledgementTemplate = (proformaId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TemplateDto>({
      method: 'POST',
      url: `/api/app/proforma/generate-acknowledgement-template/${proformaId}`,
    },
    { apiName: this.apiName,...config });
  

  generateTemplate = (proformaId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, TemplateDto>({
      method: 'POST',
      url: `/api/app/proforma/generate-template/${proformaId}`,
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProformaDto>({
      method: 'GET',
      url: `/api/app/proforma/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: '/api/app/proforma/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getCustomerOptions = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, CustomerOptionDto[]>({
      method: 'GET',
      url: '/api/app/proforma/customer-options',
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      url: '/api/app/proforma/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getEmailsByCustomerIdAndEmailTypeStr = (customerId: string, emailTypeStr: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: `/api/app/proforma/emails/${customerId}`,
      params: { emailTypeStr },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProformaDto[]>({
      method: 'GET',
      url: `/api/app/proforma/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      url: `/api/app/proforma/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getFullListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProformaListDto[]>({
      method: 'GET',
      url: '/api/app/proforma/full-list-with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: '/api/app/proforma/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<ProformaDto>>({
      method: 'GET',
      url: '/api/app/proforma',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<ProformaListDto>>({
      method: 'GET',
      url: '/api/app/proforma/with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/proforma/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      url: '/api/app/proforma/schema',
    },
    { apiName: this.apiName,...config });
  

  getVesselContactsEmailsByVesselIdAndEmailTypeStr = (vesselId: string, emailTypeStr: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: `/api/app/proforma/vessel-contacts-emails/${vesselId}`,
      params: { emailTypeStr },
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProformaDto>({
      method: 'GET',
      url: `/api/app/proforma/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/proforma/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdateProformaDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProformaDto>({
      method: 'PUT',
      url: `/api/app/proforma/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateBankDetailsByIdAndDomesticBankIdAndForeignBankId = (id: string, domesticBankId: string, foreignBankId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'PUT',
      url: `/api/app/proforma/${id}/bank-details`,
      params: { domesticBankId, foreignBankId },
    },
    { apiName: this.apiName,...config });
  

  updateProformaAddressFromCustomer = (proformaId: string, customerId: string, isProspective?: boolean, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'PUT',
      url: '/api/app/proforma/proforma-address-from-customer',
      params: { proformaId, customerId, isProspective },
    },
    { apiName: this.apiName,...config });
  

  updateServiceRequestNoByIdAndServiceRequestNo = (id: string, serviceRequestNo: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'PUT',
      url: `/api/app/proforma/${id}/service-request-no`,
      params: { serviceRequestNo },
    },
    { apiName: this.apiName,...config });
  

  updateStatusChange = (id: string, status: ProformaStatus, sendDate: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'PUT',
      url: `/api/app/proforma/${id}/status-change`,
      params: { status, sendDate },
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
